import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Form, Select, Switch, Input } from "antd";
import columns from "../../columns/team/column";
import SkeletonTable from "../../components/table/SkeletonTable";
import Edit from "../../assets/Edit";
import LoadableButton from "../../components/button/LoadableButton";
import ModalComponent from "../../components/modals/ModalComponent";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { getAllPipelineApi } from "../../api/request/pipeline";
import {
  AssignPermissionApi,
  ChangeEmployeeStatusApi,
  getAllTeamApi,
} from "../../api/request/team";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import EditRoleModal from "./components/EditRoleModal";
import EditUser from "../../assets/SettingUser";
import StatusBadge from "../../components/common/StatusBadge";
import UserPermission from "../../assets/UserPermission";
import EditUserDetailModal from "./components/EditUserDetailModal";
import Report from "../../assets/Report";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const Team = () => {
  const [employeeList, setEmployeeList] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [isPermissionOpen, setIsPermissionOpen] = useState(false);
  const [pipelineList, setPipelineList] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [permissionList, setPermissionList] = useState([]);
  const [isAssignLoading, setIsAssignLoading] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [IsEditInformation, setIsEditInformation] = useState(false);
  const navigate = useNavigate();

  const getTeamList = async () => {
    try {
      setIsLoading(true);
      const { data } = await getAllTeamApi();
      if (data.status) {
        setEmployeeList(
          data?.data?.map((item) => {
            return {
              ...item,
              name: <span className="capitalize">{item.name}</span>,
              status: (
                <Switch
                  checked={item?.status === "active"}
                  onClick={() => handleEmployeeStatus(item)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              ),
              role: (
                <StatusBadge
                  status={item?.role === "admin" ? "sub admin" : "employee"}
                />
              ),
              action: (
                <>
                  <div className="flex gap-3">
                    <GlobalTooltip title={"Assign Permission"}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmployee(item);
                          setIsPermissionOpen(true);
                        }}
                      >
                        <UserPermission />
                      </button>
                    </GlobalTooltip>
                    <GlobalTooltip title={"Assign Role"}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmployee(item);
                          setIsRoleModalOpen(true);
                        }}
                      >
                        <EditUser />
                      </button>
                    </GlobalTooltip>

                    <GlobalTooltip title={"Edit Profile"}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmployee(item);
                          setIsEditInformation(true);
                        }}
                      >
                        <Edit />
                      </button>
                    </GlobalTooltip>

                    <GlobalTooltip title={"Report"}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/team/report/${item.id}`);
                        }}
                      >
                        <Report />
                      </button>
                    </GlobalTooltip>
                  </div>
                </>
              ),
            };
          })
        );
        setTotal(data?.total_record);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  const getPipelineList = async () => {
    try {
      const { data } = await getAllPipelineApi();
      if (data?.status) {
        setPipelineList(data?.data);
        if (data?.data?.length) setPipelineId(data?.data[0]?.id);
      }
    } catch (err) {}
  };

  const clearData = () => {
    setPermissionList([]);
    setSelectedEmployee({});
  };

  useEffect(() => {
    getTeamList();
    getPipelineList();
  }, []);

  useEffect(() => {
    if (!isPermissionOpen) clearData();
  }, [isPermissionOpen]);

  useEffect(() => {
    if (pipelineId) {
      const pipeline = pipelineList.find((item) => item?.id === pipelineId);
      const permissions = pipeline?.pipeline_manage_tasks
        ?.map(({ id, name, orders }) => {
          const hasPermission = selectedEmployee?.permissions?.some(
            (secondItem) => secondItem?.id === id
          );
          return { id, name, orders, permission: hasPermission };
        })
        ?.sort((a, b) => a.orders - b.orders);
      setPermissionList(permissions);
    }
  }, [pipelineId, selectedEmployee]);

  const togglePermission = (id) => {
    setPermissionList(
      permissionList?.map((item) => {
        if (item?.id === id) {
          return { ...item, permission: !item.permission };
        }
        return item;
      })
    );
  };

  const getPermissionIds = () => {
    const existingPermissions =
      selectedEmployee?.permissions
        ?.filter(
          (item) =>
            !permissionList?.some((secondItem) => secondItem?.id === item?.id)
        )
        ?.map((item) => item.id) || [];

    const newPermissions =
      permissionList
        ?.filter((item) => item.permission === true)
        ?.map((item) => item.id) || [];

    return [...new Set([...existingPermissions, ...newPermissions])];
  };

  const assignPermission = async () => {
    try {
      setIsAssignLoading(true);
      let params = {
        employee_id: selectedEmployee?.id,
        pipeline_manage_id: getPermissionIds(),
      };
      const { data } = await AssignPermissionApi(params);
      if (data?.status) {
        clearData();
        toast.success(data?.message);
        setIsPermissionOpen(false);
        getTeamList();
      }
      setIsAssignLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsAssignLoading(false);
    }
  };

  const handleEmployeeStatus = (employee) => {
    setSelectedEmployee(employee);
    setIsStatusModalOpen(true);
  };

  const updateEmployeeStatus = (id, status) => {
    setEmployeeList((prev) => {
      return prev.map((item) => {
        if (item?.id === id) {
          return {
            ...item,
            status: (
              <Switch
                checked={status === "active"}
                onClick={() => handleEmployeeStatus({ ...item, status })}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            ),
          };
        }
        return item;
      });
    });
  };

  const handleStatusChange = async () => {
    try {
      setIsStatusLoading(true);
      const status =
        selectedEmployee?.status === "active" ? "inactive" : "active";
      const params = { id: selectedEmployee?.id, status };
      const { data } = await ChangeEmployeeStatusApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsStatusModalOpen(false);
        setSelectedEmployee(null);
        updateEmployeeStatus(selectedEmployee?.id, status);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsStatusLoading(false);
    }
  };

  return (
    <div>
      <div className="border rounded-lg bg-White pb-5">
        <div className="flex items-center justify-between p-5">
          <p className="title">Team {total ? `(${total})` : null}</p>
        </div>

        <SkeletonTable
          columns={columns}
          searchingFields={["name", "email"]}
          data={employeeList}
          isLoading={isLoading}
          onRowClick={(record) => {
            setSelectedEmployee(record);
            setIsPermissionOpen(true);
          }}
        />
      </div>

      {/* Employee permission modal */}
      <ModalComponent
        isOpen={isPermissionOpen}
        setIsOpen={setIsPermissionOpen}
        title="Employee Permissions"
        width={600}
      >
        <Select
          placeholder="Select Pipeline"
          size="large"
          className="w-full mb-5"
          value={pipelineId}
          onChange={(value) => setPipelineId(value)}
        >
          {pipelineList?.map((item) => (
            <Option value={item?.id}>{item?.name}</Option>
          ))}
        </Select>

        <div className="grid grid-cols-2 sm:grid-cols-3 sm:gap-5 gap-3">
          {permissionList?.map((item, index) => (
            <div
              key={index}
              className={`p-3 font-medium border-2 rounded-md cursor-pointer duration-500 ${
                item?.permission ? "border-primary" : ""
              }`}
              onClick={() => togglePermission(item?.id)}
            >
              <div
                className={`mx-auto flex items-center justify-center size-9 border-2 rounded-md ${
                  item?.permission ? "border-primary" : ""
                }`}
              >
                <p className="title text-primary">{item?.orders}</p>
              </div>
              <p className="text-center text-black mt-3">{item?.name}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10">
          <LoadableButton
            type="submit"
            lable="Assign Permission"
            onClick={assignPermission}
            loadingLable="Assigning permission..."
            isLoading={isAssignLoading}
          />
        </div>
      </ModalComponent>

      {/* Employee Edit Details */}
      <EditUserDetailModal
        selectedEmployee={selectedEmployee}
        editInformation={IsEditInformation}
        setEditInformation={setIsEditInformation}
        getTeamList={getTeamList}
      />

      {/* Confirmation modal for status */}
      <ConfirmationModal
        isOpen={isStatusModalOpen}
        setIsOpen={setIsStatusModalOpen}
        message="Are you sure, you want to change employee status?"
        onConfirm={handleStatusChange}
        isLoading={isStatusLoading}
        loadingLabel="Changing status..."
      />

      {/* Role Change Modal */}

      <EditRoleModal
        isOpen={isRoleModalOpen}
        setIsOpen={setIsRoleModalOpen}
        user={selectedEmployee}
        getTeamList={getTeamList}
      />
    </div>
  );
};

export default Team;
